<template>
  <div class="instruction-step-card">
    <a
      v-if="hasVideoLink"
      href="#"
      class="instruction-step-card-timestamp-link"
      @click.prevent="handleTimestampClick"
    >
      {{ formattedTimestamp }}
    </a>
    <div
      class="instruction-step-card-content-wrapper"
      :class="{ 'instruction-step-card-content-wrapper--readonly': readOnly }"
      @click="handleCardClick"
    >
      <InspectorActionLine
        :competence_category="competence_category"
        :inspector_action="inspector_action"
      />
      <div class="instruction-step-card-criteria-wrapper">
        <VideoInstructionStep
          :look_for="look_for"
          :pass_criteria="pass_criteria"
          :fail_criteria="fail_criteria"
          action=""
          timestamp_ref=""
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import InspectorActionLine from "@components/InspectorActionLine/InspectorActionLine.vue";
import VideoInstructionStep from "@components/VideoInstructionStep/VideoInstructionStep.vue";

const props = defineProps({
  competence_category: {
    type: String,
    required: true,
    validator: (value) => ["knowledge", "ability", "behaviour"].includes(value),
  },
  inspector_action: {
    type: String,
    required: true,
  },
  look_for: {
    type: String,
    required: true,
  },
  pass_criteria: {
    type: String,
    required: true,
  },
  fail_criteria: {
    type: String,
    required: true,
  },
  timestamp_ref: {
    type: String,
    required: true,
  },
  video_jump_url: {
    type: String,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["timestamp-click", "card-click"]);

const hasVideoLink = computed(() => {
  return Boolean(props.timestamp_ref && props.video_jump_url);
});

/** Trim leading zero on end segment when shaped like “01:22” → “1:22” (not “0:19”). */
function trimEndTimeDisplay(raw) {
  const t = typeof raw === "string" ? raw.trim() : "";
  if (!t) return "";
  return t.replace(/^0(\d:\d{2})$/, "$1");
}

const formattedTimestamp = computed(() => {
  if (!props.timestamp_ref) return "";
  const parts = props.timestamp_ref.split("-");
  if (parts.length === 2) {
    const start = parts[0].trim();
    const end = trimEndTimeDisplay(parts[1]);
    return `${start} - ${end}`;
  }
  return props.timestamp_ref;
});

// Emit timestamp click event to parent for video navigation
const handleTimestampClick = () => {
  emit("timestamp-click", props.video_jump_url);
};

// Emit card-click event when card content is clicked
const handleCardClick = () => {
  if (props.readOnly) return;
  emit("card-click");
};
</script>

<style scoped>
.instruction-step-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.instruction-step-card-timestamp-link {
  color: #355ffb;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;
  padding-left: 4px;
}

/* Hide VideoInstructionStep's timestamp column and main-action */
.instruction-step-card-criteria-wrapper :deep(.video-instruction-step-flex) {
  padding: 0;
  gap: 0;
}

.instruction-step-card-criteria-wrapper :deep(.timestamp-col) {
  display: none;
}

.instruction-step-card-criteria-wrapper :deep(.main-action) {
  display: none;
}

.instruction-step-card-criteria-wrapper :deep(.details-col) {
  flex: 1;
}
.instruction-step-card-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  width: fit-content;
}
.instruction-step-card-content-wrapper:hover {
  background-color: #ebe9fe;
  cursor: url("../assets/cursorArrow.svg"), auto;
  border-radius: 4px;
}

.instruction-step-card-content-wrapper--readonly,
.instruction-step-card-content-wrapper--readonly:hover {
  background-color: transparent;
  cursor: default;
}
</style>
